// =============================================================================
// Date Formatting Utilities
// All dates displayed in Pacific Standard Time (PST)
// =============================================================================

const PST_TIMEZONE = 'America/Los_Angeles';

/**
 * Format date as "Jan 15, 2026" in PST
 */
export function formatDate(date: string | Date | null | undefined): string {
  if (!date) return '';
  const d = typeof date === 'string' ? new Date(date) : date;
  return d.toLocaleDateString('en-US', {
    timeZone: PST_TIMEZONE,
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/**
 * Format date as "January 15, 2026" in PST (long format)
 */
export function formatDateLong(date: string | Date | null | undefined): string {
  if (!date) return '';
  const d = typeof date === 'string' ? new Date(date) : date;
  return d.toLocaleDateString('en-US', {
    timeZone: PST_TIMEZONE,
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

/**
 * Format date and time as "Jan 15, 2026, 3:45 PM PST" in PST
 */
export function formatDateTime(date: string | Date | null | undefined): string {
  if (!date) return '';
  const d = typeof date === 'string' ? new Date(date) : date;
  return d.toLocaleString('en-US', {
    timeZone: PST_TIMEZONE,
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZoneName: 'short',
  });
}

/**
 * Format time only as "3:45 PM PST" in PST
 */
export function formatTime(date: string | Date | null | undefined): string {
  if (!date) return '';
  const d = typeof date === 'string' ? new Date(date) : date;
  return d.toLocaleTimeString('en-US', {
    timeZone: PST_TIMEZONE,
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZoneName: 'short',
  });
}

/**
 * Format date as "01/15/2026" in PST
 */
export function formatDateShort(date: string | Date | null | undefined): string {
  if (!date) return '';
  const d = typeof date === 'string' ? new Date(date) : date;
  return d.toLocaleDateString('en-US', {
    timeZone: PST_TIMEZONE,
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
  });
}

/**
 * Format relative time (e.g., "2 hours ago", "Yesterday")
 * Falls back to date format for older dates
 */
export function formatRelativeTime(date: string | Date | null | undefined): string {
  if (!date) return '';
  const d = typeof date === 'string' ? new Date(date) : date;
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins} min ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;

  return formatDate(d);
}

/**
 * Format date for message lists - shows time if today, date otherwise
 */
export function formatMessageDate(date: string | Date | null | undefined): string {
  if (!date) return '';
  const d = typeof date === 'string' ? new Date(date) : date;
  const now = new Date();

  // Convert both to PST for comparison
  const pstOptions = { timeZone: PST_TIMEZONE };
  const dateStr = d.toLocaleDateString('en-US', pstOptions);
  const nowStr = now.toLocaleDateString('en-US', pstOptions);

  if (dateStr === nowStr) {
    // Today - show time
    return d.toLocaleTimeString('en-US', {
      timeZone: PST_TIMEZONE,
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    }) + ' PST';
  }

  // Not today - show date
  return formatDate(d);
}
